import React, { useEffect, useState, useCallback, useMemo } from "react";
import axios from "axios";
import {
  Plus, Trash2, Users, CheckCircle, XCircle, X, MapPin, Calendar, Tag,
  Building2, ExternalLink, Lock, Globe as GlobeIcon, Search, Briefcase, Eye,
  FileSpreadsheet, Download,
} from "lucide-react";
import { useLanguage } from "../../i18n/LanguageContext";
import { useIsAdmin } from "../../context/AuthContext";
import SideFilters from "../../components/SideFilters";
import EventMiniMap from "../../components/EventMiniMap";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const EMPTY = {
  name: "", date: "", location: "", sector: "",
  host_company: "", visibility: "Pubblico",
  description: "", future_presence: false, attendees: [],
};

function EventDetailModal({ event, onClose }) {
  if (!event) return null;
  const att = Array.isArray(event.attendees) ? event.attendees : [];
  const isPrivate = (event.visibility || "").toLowerCase().startsWith("priv");
  const VisIcon = isPrivate ? Lock : GlobeIcon;
  const visColor = isPrivate ? "#B45309" : "#059669";
  return (
    <div data-testid="b2b-event-modal" className="fixed inset-0 flex z-[9999]" onClick={onClose}>
      <div className="flex-1 bg-black/80 backdrop-blur-sm" />
      <div onClick={(e) => e.stopPropagation()}
        className="h-full w-full max-w-[860px] bg-[#0A0C10] border-l-2 border-[#A78BFA] flex flex-col overflow-hidden shadow-[0_0_40px_rgba(0,0,0,0.6)]">
        <div className="flex items-center gap-3 px-6 py-5 border-b border-white/[0.04] bg-[#0D0F12]">
          <div className="w-12 h-12 flex items-center justify-center border-2 border-[#A78BFA] bg-[#A78BFA]/10 flex-shrink-0">
            <Users className="w-6 h-6 text-[#A78BFA]" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-lg font-bold text-white truncate" style={{ fontFamily: "Chivo, sans-serif" }}>
              {event.name}
            </h2>
            <div className="flex items-center gap-3 mt-1 flex-wrap text-xs text-[#CBD5E1]">
              <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 border font-bold tracking-widest uppercase"
                style={{ color: visColor, borderColor: `${visColor}66`, background: `${visColor}15` }}>
                <VisIcon className="w-3 h-3" />{event.visibility || "Pubblico"}
              </span>
              {event.host_company && <span className="flex items-center gap-1"><Building2 className="w-3 h-3" />{event.host_company}</span>}
              {event.date && <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{event.date}</span>}
              {event.location && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{event.location}</span>}
              {event.sector && <span className="flex items-center gap-1"><Tag className="w-3 h-3" />{event.sector}</span>}
            </div>
          </div>
          <button data-testid="b2b-event-modal-close" onClick={onClose}
            className="text-[#CBD5E1] hover:text-white hover:bg-[#1F242D] p-2 transition-colors flex-shrink-0">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          {/* Export bar */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] tracking-widest uppercase text-[#A0AEC0]">Esporta lista:</span>
            <button
              data-testid="export-b2b-event-csv"
              onClick={() => window.open(`${API}/network/events/${event.id}/attendees.csv`, "_blank")}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] tracking-widest uppercase font-bold bg-[#059669] text-white hover:bg-[#047857] transition-colors"
            >
              <FileSpreadsheet className="w-3 h-3" /> CSV
            </button>
            <button
              data-testid="export-b2b-event-pdf"
              onClick={() => window.open(`${API}/network/events/${event.id}/attendees.pdf`, "_blank")}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] tracking-widest uppercase font-bold bg-[#A78BFA] text-black hover:bg-[#8B6FE0] transition-colors"
            >
              <Download className="w-3 h-3" /> PDF
            </button>
          </div>

          {event.description && (
            <section className="bg-[#0D0F12] border border-white/[0.04] p-5">
              <p className="text-[11px] text-[#A0AEC0] tracking-widest uppercase mb-2">Informazioni sull&apos;Evento</p>
              <p className="text-sm text-[#D1D5DB] leading-relaxed">{event.description}</p>
            </section>
          )}

          <section className="bg-[#0D0F12] border border-white/[0.04]">
            <div className="flex items-center gap-2 px-5 py-3 border-b border-white/[0.04]">
              <Users className="w-4 h-4 text-[#A78BFA]" />
              <p className="text-xs text-[#A0AEC0] tracking-widest uppercase">
                {att.length} partecipanti
              </p>
            </div>
            {att.length === 0 ? (
              <div className="text-center py-8 text-xs text-[#A0AEC0]">Nessun partecipante registrato per questo evento.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-[#A0AEC0] tracking-widest uppercase border-b border-white/[0.04]">
                      {["Azienda", "Paese", "Settore", "Ruolo", "Sito"].map(h => (
                        <th key={h} className="text-left px-4 py-2.5 font-normal">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {att.map((a, i) => (
                      <tr key={`${a.name}-${i}`} data-testid="attendee-row"
                        className="border-b border-white/[0.04] last:border-0 hover:bg-[#1A1D24] transition-colors">
                        <td className="px-4 py-2.5 text-white font-medium">{a.name}</td>
                        <td className="px-4 py-2.5 text-[#CBD5E1]">{a.country || "—"}</td>
                        <td className="px-4 py-2.5 text-[#CBD5E1]">{a.sector || "—"}</td>
                        <td className="px-4 py-2.5 text-[#00E5FF] font-mono">{a.role || "—"}</td>
                        <td className="px-4 py-2.5">
                          {a.website ? (
                            <a href={a.website} target="_blank" rel="noopener noreferrer"
                              data-testid="attendee-website"
                              className="inline-flex items-center gap-1 text-[#A78BFA] hover:underline">
                              {a.website.replace(/^https?:\/\//, "").replace(/\/$/, "")}
                              <ExternalLink className="w-3 h-3" />
                            </a>
                          ) : <span className="text-[#A0AEC0]">—</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}

export default function NetworkEvents() {
  const { t } = useLanguage();
  const isAdmin = useIsAdmin();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({});
  const [filtersOpen, setFiltersOpen] = useState(() => typeof window !== "undefined" && window.innerWidth >= 1024);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/network/events`, { withCredentials: true });
      setItems(Array.isArray(data) ? data : []);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, attendees: form.attendees };
      await axios.post(`${API}/network/events`, payload, { withCredentials: true });
      setForm(EMPTY);
      setShowAdd(false);
      await load();
    } finally { setSaving(false); }
  };

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    await axios.delete(`${API}/network/events/${id}`, { withCredentials: true });
    await load();
  };

  // ── Filtered items ───────────────────────────────────────────────
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const fSectors = filters.sector || [];
    const fVis = filters.visibility || [];
    const fHosts = filters.host_company || [];
    const fLocation = (filters.location || "").trim().toLowerCase();
    const fFuture = filters.future_presence === true;

    return items.filter(ev => {
      if (q && ![ev.name, ev.host_company, ev.location, ev.sector, ev.description].some(v => (v || "").toLowerCase().includes(q))) return false;
      if (fSectors.length && !fSectors.includes(ev.sector)) return false;
      if (fVis.length && !fVis.includes(ev.visibility)) return false;
      if (fHosts.length && !fHosts.includes(ev.host_company)) return false;
      if (fLocation && !(ev.location || "").toLowerCase().includes(fLocation)) return false;
      if (fFuture && !ev.future_presence) return false;
      return true;
    });
  }, [items, search, filters]);

  // ── Facet sections ────────────────────────────────────────────────
  const facetSections = useMemo(() => {
    const sectorMap = new Map();
    const hostMap = new Map();
    const visMap = new Map();
    items.forEach(ev => {
      if (ev.sector) sectorMap.set(ev.sector, (sectorMap.get(ev.sector) || 0) + 1);
      if (ev.host_company) hostMap.set(ev.host_company, (hostMap.get(ev.host_company) || 0) + 1);
      const v = ev.visibility || "Pubblico";
      visMap.set(v, (visMap.get(v) || 0) + 1);
    });
    const sectorOpts = Array.from(sectorMap.entries())
      .sort((a, b) => b[1] - a[1]).slice(0, 20)
      .map(([s, n]) => ({ value: s, label: s, count: n }));
    const hostOpts = Array.from(hostMap.entries())
      .sort((a, b) => b[1] - a[1]).slice(0, 30)
      .map(([h, n]) => ({ value: h, label: h, count: n }));
    const visOpts = Array.from(visMap.entries())
      .sort((a, b) => b[1] - a[1])
      .map(([v, n]) => ({ value: v, label: v, count: n }));

    return [
      { id: "sector",       label: "Settore",           icon: Briefcase, type: "checkbox", options: sectorOpts },
      { id: "visibility",   label: "Visibilità",        icon: Eye,       type: "checkbox", options: visOpts },
      { id: "host_company", label: "Azienda ospitante", icon: Building2, type: "checkbox", options: hostOpts },
      { id: "location",     label: "Luogo",             icon: MapPin,    type: "text",     placeholder: "es. Milano, Roma…" },
      { id: "future_presence", label: "Presenza futura", icon: CheckCircle, type: "toggle", toggleLabel: "Solo eventi futuri" },
    ];
  }, [items]);

  return (
    <div className="p-4 sm:p-6 space-y-6">
      {/* Header */}
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-white tracking-tight" style={{ fontFamily: "Chivo, sans-serif" }}>{t("net.events.title")}</h1>
          <p className="text-xs text-[#A0AEC0] mt-1 tracking-widest uppercase">{t("net.events.subtitle")}</p>
        </div>
        <button
          data-testid="add-b2b-event-button"
          onClick={() => setShowAdd(!showAdd)}
          className={`flex items-center gap-2 bg-[#A78BFA] text-black font-semibold px-4 py-2 text-xs hover:bg-[#8B6FE0] transition-colors ${isAdmin ? "" : "hidden"}`}>
          <Plus className="w-3.5 h-3.5" /> Add B2B Event
        </button>
      </div>

      {/* Mini map */}
      <EventMiniMap
        events={filtered}
        accent="#A78BFA"
        Icon={Users}
        title="Mappa B2B Events"
        maxItems={12}
        onEventClick={(ev) => setSelected(ev)}
      />

      {showAdd && isAdmin && (
        <form onSubmit={handleAdd} className="bg-[#0D0F12] border border-white/[0.04] p-5 space-y-4">
          <p className="text-xs text-[#A0AEC0] tracking-widest uppercase border-b border-white/[0.04] pb-3 mb-4">Nuovo B2B Event</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              ["name", "Nome Evento *"],
              ["date", "Data (YYYY-MM-DD)"],
              ["location", "Luogo"],
              ["sector", "Settore"],
              ["host_company", "Azienda Ospitante *"],
            ].map(([f, l]) => (
              <div key={f}>
                <label className="block text-xs text-[#CBD5E1] tracking-wider uppercase mb-1.5">{l}</label>
                <input
                  value={form[f]}
                  onChange={e => setForm(p => ({ ...p, [f]: e.target.value }))}
                  required={f === "name" || f === "host_company"}
                  className="w-full bg-[#050505] border border-white/[0.04] text-white px-3 py-2 text-sm focus:outline-none focus:border-[#A78BFA]"
                />
              </div>
            ))}
            <div>
              <label className="block text-xs text-[#CBD5E1] tracking-wider uppercase mb-1.5">Visibilità</label>
              <select
                value={form.visibility}
                onChange={e => setForm(p => ({ ...p, visibility: e.target.value }))}
                className="w-full bg-[#050505] border border-white/[0.04] text-white px-3 py-2 text-sm focus:outline-none focus:border-[#A78BFA]">
                <option value="Pubblico">Pubblico</option>
                <option value="Privato">Privato</option>
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs text-[#CBD5E1] tracking-wider uppercase mb-1.5">Descrizione</label>
              <textarea
                value={form.description}
                onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
                rows={3}
                className="w-full bg-[#050505] border border-white/[0.04] text-white px-3 py-2 text-sm focus:outline-none focus:border-[#A78BFA] resize-none"
              />
            </div>
          </div>
          <div className="flex items-center gap-3">
            <input type="checkbox" id="fp-b2b" checked={form.future_presence} onChange={e => setForm(p => ({ ...p, future_presence: e.target.checked }))} className="w-4 h-4 accent-[#A78BFA]" />
            <label htmlFor="fp-b2b" className="text-xs text-[#CBD5E1]">Partecipazione futura prevista</label>
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={() => setShowAdd(false)} className="px-4 py-2 text-xs bg-[#1F242D] text-white border border-white/[0.04] hover:bg-[#30363D]">Cancel</button>
            <button data-testid="save-b2b-event-button" type="submit" disabled={saving} className="px-4 py-2 text-xs bg-[#A78BFA] text-black font-semibold hover:bg-[#8B6FE0] disabled:opacity-50">{saving ? "Saving..." : "Save"}</button>
          </div>
        </form>
      )}

      {/* Body: sidebar filtri + main table */}
      <div className="flex flex-col lg:flex-row gap-4">
        <SideFilters
          open={filtersOpen} setOpen={setFiltersOpen}
          filters={filters} setFilters={setFilters}
          sections={facetSections}
          totalMatch={filtered.length} totalAll={items.length}
          entity="b2bevents"
        />

        <div className="flex-1 min-w-0 space-y-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6B7280]" />
            <input
              data-testid="b2b-events-search"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Filtra per nome, azienda ospitante, luogo, settore…"
              className="w-full bg-[#050505] border border-white/[0.04] text-white pl-10 pr-10 py-2.5 text-sm focus:outline-none focus:border-[#A78BFA] transition-colors placeholder:text-[#6B7280]"
            />
            {search && (
              <button onClick={() => setSearch("")} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-[#6B7280] hover:text-white transition-colors">
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>

          <div className="bg-[#0D0F12] border border-white/[0.04]">
            <div className="flex items-center gap-2 px-5 py-3 border-b border-white/[0.04]">
              <Users className="w-4 h-4 text-[#A78BFA]" />
              <p className="text-xs text-[#A0AEC0] tracking-widest uppercase">{filtered.length} B2B events · clicca una riga per i dettagli</p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-[#A0AEC0] tracking-widest uppercase border-b border-white/[0.04]">
                    {["Nome", "Data", "Luogo", "Settore", "Ospitato da", "Visibilità", "Partecipanti", "Presenza Futura", ...(isAdmin ? [""] : [])].map(h => (
                      <th key={h} className="text-left px-4 py-3 font-normal">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={9} className="text-center py-8 text-[#A0AEC0]">Loading...</td></tr>
                  ) : filtered.length === 0 ? (
                    <tr><td colSpan={9} className="text-center py-8 text-[#A0AEC0]">Nessun evento corrisponde ai filtri.</td></tr>
                  ) : filtered.map((item) => {
                    const isPriv = (item.visibility || "").toLowerCase().startsWith("priv");
                    const vColor = isPriv ? "#B45309" : "#059669";
                    const VIcon = isPriv ? Lock : GlobeIcon;
                    return (
                      <tr key={item.id} data-testid="b2b-event-row" onClick={() => setSelected(item)}
                        className="border-b border-white/[0.04] hover:bg-[#1A1D24] cursor-pointer transition-colors">
                        <td className="px-4 py-3 text-white font-medium hover:text-[#A78BFA]">{item.name}</td>
                        <td className="px-4 py-3 text-[#CBD5E1]">{item.date}</td>
                        <td className="px-4 py-3 text-[#CBD5E1]">{item.location}</td>
                        <td className="px-4 py-3 text-[#CBD5E1]">{item.sector}</td>
                        <td className="px-4 py-3 text-[#CBD5E1]">{item.host_company || "—"}</td>
                        <td className="px-4 py-3">
                          <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 border font-bold tracking-widest uppercase"
                            style={{ color: vColor, borderColor: `${vColor}66`, background: `${vColor}15` }}>
                            <VIcon className="w-3 h-3" />{item.visibility || "Pubblico"}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-[#A78BFA] font-medium">{(item.attendees || []).length}</td>
                        <td className="px-4 py-3">{item.future_presence ? <CheckCircle className="w-4 h-4 text-[#00FF66]" /> : <XCircle className="w-4 h-4 text-[#A0AEC0]" />}</td>
                        {isAdmin && (
                          <td className="px-4 py-3">
                            <button onClick={(e) => handleDelete(item.id, e)} className="text-[#A0AEC0] hover:text-[#FF3B30] transition-colors">
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <EventDetailModal event={selected} onClose={() => setSelected(null)} />
    </div>
  );
}
